import {
  apiUpdateSlide,
  apiCreateSlide,
  apiDeleteSlide,
  apiReorderSlides,
  apiUploadImage,
  apiRemoveImage,
  apiDuplicateSlide,
  apiUpdatePresentationProperties,
  apiGetPresentation
} from '../../common/api/lesson'
import {
  getPresentation,
  getOrderedSlides,
  getSlidesMap
} from './presentation.selectors'
import { getActiveSlideId } from '../../components/pages/lesson/builder/redux/builder.selectors'
import {
  BUILDER_ACTIVE_SLIDE_ID,
  LP_SHOW_LAST_SLIDE
} from 'pobble-components/dist/cookies'

const fetchPresentation = id => ({
  requestPrefix: 'PRESENTATION:FETCH',
  params: {
    id,
    showLastSlide: LP_SHOW_LAST_SLIDE.pull(),
    activeSlideId: BUILDER_ACTIVE_SLIDE_ID.pull()
  },
  request: () =>
    apiGetPresentation(id, {
      include:
        'presentation.audio,presentation.p365,presentation.policy,presentation.status,presentation.image,presentation.year_groups,presentation.recommended_until,presentation.category,presentation.year_group.order,presentation.details,presentation.slides,presentation.slide.type,presentation.slide.details,presentation.slide.private_note,presentation.names'
    })
})

export const resetPresentation = () => ({
  type: 'PRESENTATION:RESET'
})

const simulateSlideCreation = (() => {
  let id = 0
  let order = 100
  return slide => ({
    type: 'PRESENTATION:CREATE_SLIDE_REQUEST_SUCCESS',
    response: {
      data: {
        slide: {
          ...slide,
          id: id++,
          order: order++
        }
      }
    }
  })
})()

const createSlide = (data, customRequest) => (dispatch, getState) => {
  const state = getState()

  const presentationId = getPresentation(getState()).id

  const slides = getSlidesMap(state)
  const activeSlideId = getActiveSlideId(state)

  const activeSlide = slides[activeSlideId]

  const order = activeSlide.order < 2 ? 2 : activeSlide.order + 1

  return dispatch({
    params: { presentationId, data, type: data ? data.type : null },
    requestPrefix: 'PRESENTATION:CREATE_SLIDE',
    request: customRequest
      ? customRequest({ order })
      : () =>
          apiCreateSlide(
            presentationId,
            {
              ...data,
              order
            },
            {
              include: 'slide.type,slide.details'
            }
          )
  })
}

const immediateSlideUpdate = (id, data) => ({
  type: 'PRESENTATION:IMMEDIATE_UPDATE',
  id,
  data
})

const restoreSlide = id => ({
  type: 'PRESENTATION:RESTORE_SLIDE',
  id
})

const updateSlide = (id, type, data, params) => (dispatch, getState) => {
  const presentation = getPresentation(getState())

  /**
   * Asset presentation doesn't allow user to change title slide
   * Therefore we automatically update title slide whenever user
   *   changes title of an asset slide
   */
  if (presentation.asset && ['Exemplar'].indexOf(type) !== -1) {
    apiUpdateSlide(presentation.slides[0].id, {
      title: data.title
    })
  }

  return dispatch({
    requestPrefix: 'PRESENTATION:UPDATE_SLIDE',
    params: { id, data, type, ...params },
    request: () => {
      return apiUpdateSlide(id, data, {
        include: 'slide.type,slide.details'
      })
    }
  })
}

const directSlideUpdate = slide => ({
  type: 'PRESENTATION:UPDATE_SLIDE_DIRECT',
  slide
})

const updatePresentationProperties = (id, data) => ({
  requestPrefix: 'PRESENTATION:UPDATE_PROPERTIES',
  params: { id, data },
  request: () =>
    apiUpdatePresentationProperties(id, data, {
      include:
        'presentation.year_groups,presentation.year_group.order,presentation.details'
    })
})

const uploadImage = (id, data) => ({
  requestPrefix: 'PRESENTATION:UPLOAD_IMAGE_TO_SLIDE',
  params: { id, data },
  request: () =>
    apiUploadImage(id, data).then(() =>
      apiUpdateSlide(
        id,
        { use_presentation_image: false },
        {
          include: 'slide.type,slide.details'
        }
      )
    )
})

const removeImageImmediately = (id, params) => dispatch => {
  dispatch({ type: 'PRESENTATION:REMOVE_IMAGE_FROM_SLIDE', id, params })
  return dispatch(removeImage(id)).catch(e => {
    dispatch(restoreSlide(id))
    throw e
  })
}

const removeImage = id => ({
  requestPrefix: 'PRESENTATION:REMOVE_IMAGE_FROM_SLIDE',
  params: { id },
  request: () => apiRemoveImage(id)
})

const updateSlideImmediately = (id, type, data, testMode) => (
  dispatch,
  getState
) => {
  dispatch(immediateSlideUpdate(id, data))
  return (
    !testMode &&
    dispatch(updateSlide(id, type, data, { immediateUpdate: true })).catch(
      e => {
        dispatch(restoreSlide(id))
        throw e
      }
    )
  )
}

const duplicateSlide = id => ({
  requestPrefix: 'PRESENTATION:DUPLICATE_SLIDE',
  params: { id },
  request: () =>
    apiDuplicateSlide(id, {
      include:
        'presentation.slides,presentation.slide.type,presentation.slide.details,presentation.slide.private_note'
    })
})

const deleteSlide = id => (dispatch, getState) =>
  dispatch({
    requestPrefix: 'PRESENTATION:DELETE_SLIDE',
    params: { id, slides: getOrderedSlides(getState()) },
    request: () => apiDeleteSlide(id)
  })

const reorderSlidesRequest = (presentationId, slidesOrder) => ({
  requestPrefix: 'PRESENTATION:REORDER_SLIDES',
  params: { presentationId, slidesOrder },
  request: () => apiReorderSlides(presentationId, { slide_ids: slidesOrder })
})

const reorderSlides = (presentationId, slidesOrder) => dispatch => {
  dispatch({ type: 'PRESENTATION:REORDER_SLIDES', slidesOrder })
  return dispatch(reorderSlidesRequest(presentationId, slidesOrder)).catch(
    e => {
      dispatch({ type: 'PRESENTATION:RESTORE_ALL_SLIDES' })
      throw e
    }
  )
}

const updateNotes = (id, { privateNote, publicNote }) => ({
  requestPrefix: 'PRESENTATION:UPDATE_NOTES',
  params: { privateNote, publicNote },
  request: () =>
    apiUpdateSlide(
      id,
      { public_note: publicNote, private_note: privateNote },
      {
        include: 'slide.details,slide.private_note'
      }
    )
})

export {
  fetchPresentation,
  updateSlide,
  createSlide,
  deleteSlide,
  updateSlideImmediately,
  reorderSlides,
  uploadImage,
  removeImageImmediately,
  updateNotes,
  duplicateSlide,
  updatePresentationProperties,
  directSlideUpdate,
  simulateSlideCreation
}
